import { useState } from 'react'
import type { AccessStateResponse, AssessmentInquiry } from '@assessments/shared'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { IconTile } from '@/components/ui/icon-tile'
import { BarChartIcon, ClockIcon, ListChecksIcon, TargetIcon } from '@/components/ui/icons'
import { PageLayout } from '@/components/common/PageLayout'
import { BenchmarkPreview } from './BenchmarkPreview.js'
import { StatusCard } from './StatusCard.js'
import { submitInquiry } from './api.js'

const STEPS = [
  {
    icon: ListChecksIcon,
    title: 'Complete the assessment',
    body: 'Nine sections, answered once — responses carry forward each year.',
  },
  {
    icon: BarChartIcon,
    title: 'Analyze your scores',
    body: 'Your scores set against peers of comparable size, sector, and region.',
  },
  {
    icon: TargetIcon,
    title: 'Know where to invest next',
    body: 'Get clear direction on where to focus your program efforts.',
  },
]

interface Props {
  access: AccessStateResponse
  onSubmitted: (inquiry: AssessmentInquiry) => void
}

export function InquiryPage({ access, onSubmitted }: Props) {
  const [submitting, setSubmitting] = useState(false)
  const [failed, setFailed] = useState(false)

  async function submit() {
    setSubmitting(true)
    setFailed(false)
    try {
      const { inquiry } = await submitInquiry()
      onSubmitted(inquiry)
    } catch {
      setFailed(true)
    } finally {
      setSubmitting(false)
    }
  }

  if (access.inquiry) {
    return (
      <PageLayout title="Assessments">
        <StatusCard icon={ClockIcon} title="Request in progress">
          <p className="copy mx-auto max-w-2xl text-subtle" role="status">
            {access.inquiry.submittedByName} has requested access to complete an assessment and the
            request is currently in progress.
          </p>
        </StatusCard>
      </PageLayout>
    )
  }

  return (
    <PageLayout title="Assessments">
      <div className="space-y-6">
        <Card className="p-8 md:p-12">
          <div className="grid items-center gap-10 lg:grid-cols-2">
            <div>
              <h2 className="mb-6 text-4xl font-extrabold leading-tight text-heading">
                See how your program compares to your peers
              </h2>
              <p className="copy mb-8 text-body">
                Complete the Ethics Quotient® assessment and Ethisphere scores your responses
                against a benchmark of comparable organizations — category by category, so you know
                which parts of your ethics and compliance program to strengthen first.
              </p>
              {access.canSubmitInquiry ? (
                <>
                  <Button
                    size="lg"
                    className="text-base font-semibold"
                    onClick={() => void submit()}
                    disabled={submitting}
                  >
                    Request access
                  </Button>
                  <p className="label mt-4 text-body">
                    An Ethisphere team member follows up by email with next steps.
                  </p>
                  {failed && (
                    <p className="mt-2 text-sm text-danger" role="alert">
                      Couldn’t send your inquiry. Please try again.
                    </p>
                  )}
                </>
              ) : (
                <p className="text-sm text-subtle">
                  Inquiries are submitted by the organization’s own users.
                </p>
              )}
            </div>
            <BenchmarkPreview />
          </div>
        </Card>

        <Card className="p-8">
          <ul className="grid gap-8 text-center md:grid-cols-3">
            {STEPS.map(({ icon, title, body }) => (
              <li key={title}>
                <IconTile icon={icon} tone="success" className="mb-4" />
                <h3 className="tiny-headline mb-2 text-heading">{title}</h3>
                <p className="copy mx-auto max-w-xs text-body">{body}</p>
              </li>
            ))}
          </ul>
        </Card>
      </div>
    </PageLayout>
  )
}
